import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "./ui/table";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Search, Download } from "lucide-react";
import { toast } from "sonner";

// Fields copied by "Import Packaging" (user-chosen scope: 2026-02 iteration 68).
// Kept in one place so the confirm dialog + backend payload stay in sync.
const IMPORT_FIELDS = ["carton_length", "carton_width", "carton_height", "pcs_per_ctn", "gross_weight"];

function hasPackagingData(entry) {
  return IMPORT_FIELDS.some((f) => entry?.[f] != null);
}

export default function SimilarItemsDialog({ open, onOpenChange, itemNo, targetProduct, onImported }) {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [prefix, setPrefix] = useState("");
  const [descInput, setDescInput] = useState("");
  const [descQuery, setDescQuery] = useState("");
  const [importSource, setImportSource] = useState(null); // a similar item selected for import
  const [selectedTargetIds, setSelectedTargetIds] = useState(new Set());
  const [importing, setImporting] = useState(false);

  // Reset when dialog reopens.
  useEffect(() => {
    if (open) {
      setDescInput("");
      setDescQuery("");
      setImportSource(null);
      setSelectedTargetIds(new Set());
    }
  }, [open, itemNo]);

  // Debounce description input.
  useEffect(() => {
    const t = setTimeout(() => setDescQuery(descInput.trim()), 300);
    return () => clearTimeout(t);
  }, [descInput]);

  useEffect(() => {
    if (!open || (!itemNo && !descQuery)) { setItems([]); return; }
    let cancelled = false;
    setLoading(true);
    const params = {};
    if (itemNo) params.item_no = itemNo;
    if (descQuery) params.description = descQuery;
    api.get("/inventory/similar", { params })
      .then(({ data }) => {
        if (cancelled) return;
        setItems(data.items || []);
        setPrefix(data.prefix || "");
      })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load similar items"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, itemNo, descQuery]);

  const targetEntries = targetProduct?.entries || [];
  const targetHasExisting = useMemo(() => targetEntries.some(hasPackagingData), [targetEntries]);

  const openImport = (row) => {
    setImportSource(row);
    // Default: preselect all target variants (import to all). User can uncheck.
    setSelectedTargetIds(new Set(targetEntries.map((e) => e.entry_id)));
  };

  const toggleTarget = (id) => setSelectedTargetIds((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const confirmImport = async () => {
    if (!importSource || selectedTargetIds.size === 0) return;

    // Overwrite confirmation: if ANY selected target already has packaging data,
    // ask before overwriting (user requirement 2 overwrite=b).
    const selectedEntries = targetEntries.filter((e) => selectedTargetIds.has(e.entry_id));
    const overwriting = selectedEntries.some(hasPackagingData);
    if (overwriting && !window.confirm("This product already has packaging data. Overwrite the selected variants?")) {
      return;
    }

    setImporting(true);
    try {
      const { data } = await api.post("/inventory/entries/import-packaging", {
        source_entry_id: importSource.entry_id,
        target_entry_ids: [...selectedTargetIds],
      });
      toast.success(`Packaging data imported into ${data.updated} variant${data.updated === 1 ? "" : "s"}`);
      setImportSource(null);
      onImported && onImported();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-6xl max-h-[85vh] overflow-y-auto" data-testid="similar-items-dialog">
        <DialogHeader>
          <DialogTitle>Similar Items for <span className="font-mono">{itemNo || "-"}</span></DialogTitle>
          <DialogDescription>
            Exact match plus any item whose Item No starts with <span className="font-mono font-semibold">{prefix || "-"}</span>.
            Type below to broaden by <span className="font-semibold">Item No</span> or <span className="font-semibold">Description</span> keywords (fuzzy match, both fields). Use <span className="font-semibold">Import</span> to copy Carton L/W/H, Pcs/Ctn and Gross Weight into this product.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search size={13} strokeWidth={1.75} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="similar-items-desc-search"
            value={descInput}
            onChange={(e) => setDescInput(e.target.value)}
            placeholder="Search by Item No or Description (space-separated keywords, e.g. 'K24 blackout')"
            className="pl-8 rounded-lg"
          />
        </div>

        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">Loading...</div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400" data-testid="similar-items-empty">No similar items found.</div>
        ) : (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50 border-b border-gray-200">
                  <TableHead className="w-16 text-xs uppercase tracking-label text-gray-500">Photo</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500">Item No</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500">Description</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500">Size</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500">Contract</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right">Carton L×W×H (cm)</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right">PCS/CTN</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right">Gross Wt (kg)</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right">40' Qty</TableHead>
                  <TableHead className="text-xs uppercase tracking-label text-gray-500 text-right">Factory Cost (RMB)</TableHead>
                  <TableHead className="w-24 text-xs uppercase tracking-label text-gray-500 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.entry_id} className={`border-b border-gray-100 ${it.is_exact_match ? "bg-brand-50/40" : ""}`} data-testid={`similar-item-row-${it.entry_id}`}>
                    <TableCell className="py-2">
                      {it.photo_url ? (
                        <img src={it.photo_url} alt={it.item_no} className="w-12 h-12 object-cover rounded-lg" />
                      ) : (
                        <div className="w-12 h-12 bg-gray-50 rounded-lg" />
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs font-bold text-gray-800">
                      {it.item_no}
                      {it.is_exact_match && (
                        <span className="ml-1.5 text-[9px] uppercase tracking-label text-brand-700 bg-brand-100 border border-brand-200 rounded px-1 py-0.5">exact</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-700 max-w-[280px] truncate" title={it.description || ""}>{it.description || "-"}</TableCell>
                    <TableCell className="font-mono text-xs text-gray-600 max-w-[140px] truncate" title={it.size || ""}>{it.size || "-"}</TableCell>
                    <TableCell className="font-mono text-xs text-gray-600">
                      <div className="font-semibold text-gray-800">{it.contract_no || "-"}</div>
                      {it.contract_date && <div className="text-[10px] text-gray-400 mt-0.5">{it.contract_date}</div>}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-right text-gray-600">
                      {it.carton_length && it.carton_width && it.carton_height ? `${it.carton_length}×${it.carton_width}×${it.carton_height}` : "-"}
                    </TableCell>
                    <TableCell className="font-mono text-sm text-right text-gray-600">{it.pcs_per_ctn ?? "-"}</TableCell>
                    <TableCell className="font-mono text-sm text-right text-gray-600">{it.gross_weight ?? "-"}</TableCell>
                    <TableCell className="font-mono text-sm text-right text-gray-600">{it.qty_40ft != null ? it.qty_40ft.toLocaleString() : "-"}</TableCell>
                    <TableCell className="font-mono text-sm text-right font-bold text-gray-800">{it.purchase_price != null ? `¥${Number(it.purchase_price).toFixed(2)}` : "-"}</TableCell>
                    <TableCell className="text-right">
                      <button
                        data-testid={`similar-item-import-${it.entry_id}`}
                        onClick={() => openImport(it)}
                        disabled={!targetProduct || !hasPackagingData(it)}
                        title={hasPackagingData(it) ? "Import packaging data into current product" : "This item has no packaging data to import"}
                        className="inline-flex items-center gap-1 text-xs text-brand-700 hover:bg-brand-50 rounded-md px-2 py-1 transition-colors disabled:text-gray-300 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                      >
                        <Download size={12} strokeWidth={1.75} />
                        Import
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Import target picker: opens on top of main dialog when user clicks Import on a source row. */}
    <Dialog open={!!importSource} onOpenChange={(v) => { if (!v) setImportSource(null); }}>
      <DialogContent className="rounded-xl sm:max-w-lg" data-testid="import-packaging-dialog">
        <DialogHeader>
          <DialogTitle>Import Packaging Data</DialogTitle>
          <DialogDescription>
            Copy Carton L×W×H, Pcs/Ctn and Gross Weight from
            <span className="font-mono font-semibold text-gray-900"> {importSource?.item_no}</span> into the selected variant{targetEntries.length === 1 ? "" : "s"} of the current product.
            {targetHasExisting && <span className="block mt-1 text-amber-700">Warning: some variants already have packaging data — you'll be asked to confirm before overwriting.</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-64 overflow-y-auto">
          {targetEntries.length === 0 && (
            <div className="p-4 text-sm text-gray-400">No variants found on the current product.</div>
          )}
          {targetEntries.map((e) => (
            <label key={e.entry_id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50" data-testid={`import-target-${e.entry_id}`}>
              <Checkbox
                checked={selectedTargetIds.has(e.entry_id)}
                onCheckedChange={() => toggleTarget(e.entry_id)}
                data-testid={`import-target-checkbox-${e.entry_id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-xs font-bold text-gray-800">{e.item_no}</div>
                <div className="text-xs text-gray-500 truncate">
                  {e.size || "-"}
                  {hasPackagingData(e) && <span className="ml-2 text-amber-600">(has existing data)</span>}
                </div>
              </div>
              <div className="text-xs font-mono text-gray-400">
                {e.carton_length && e.carton_width && e.carton_height ? `${e.carton_length}×${e.carton_width}×${e.carton_height}` : "-"}
                {" · "}
                {e.pcs_per_ctn ?? "-"} pcs
              </div>
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setImportSource(null)} className="rounded-lg">Cancel</Button>
          <Button
            onClick={confirmImport}
            disabled={importing || selectedTargetIds.size === 0}
            className="rounded-lg"
            data-testid="import-packaging-confirm"
          >
            {importing ? "Importing..." : `Import to ${selectedTargetIds.size} variant${selectedTargetIds.size === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
